/**
 * Shared style constants and helpers used across all V2 CRUD pages.
 *
 * Extracted from V2Users, V2Roles, V2Sites, V2Views, V2Connectors,
 * and V2SummaryTemplates to eliminate duplication.
 */

// ---------------------------------------------------------------------------
// Flyout drawer width
// ---------------------------------------------------------------------------

export const DRAWER_WIDTH = 420;

// ---------------------------------------------------------------------------
// Toolbar action button styling (CSS custom properties safe)
// ---------------------------------------------------------------------------

export function toolbarBtnSx(disabled: boolean, danger = false) {
  const base = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    borderRadius: "6px",
    fontSize: "0.8125rem",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
  if (danger) {
    return {
      ...base,
      border: disabled ? "1px solid var(--border-mid)" : "1px solid rgba(251,113,133,0.4)",
      bgcolor: disabled ? "var(--bg-raised)" : "rgba(251,113,133,0.1)",
      color: disabled ? "var(--text-tertiary)" : "var(--signal-rose)",
      "&:hover": disabled ? {} : { bgcolor: "rgba(251,113,133,0.2)", color: "#FCA5A5" },
    };
  }
  return {
    ...base,
    border: "1px solid var(--border-mid)",
    bgcolor: "var(--bg-raised)",
    color: disabled ? "var(--text-tertiary)" : "var(--text-secondary)",
    "&:hover": disabled ? {} : { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
  };
}

// ---------------------------------------------------------------------------
// Flyout input / label styling (uses CSS custom properties)
// ---------------------------------------------------------------------------

export const flyoutInputSx = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border-mid)",
  backgroundColor: "var(--bg-raised)",
  color: "var(--text-primary)",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box" as const,
  "&:focus": {
    borderColor: "var(--accent)",
  },
  "&::placeholder": {
    color: "var(--text-tertiary)",
  },
};

export const flyoutLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--text-tertiary)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  mb: "4px",
};

export const flyoutSelectSx = {
  ...flyoutInputSx,
  appearance: "auto" as const,
  cursor: "pointer",
};

export const flyoutTextareaSx = {
  ...flyoutInputSx,
  resize: "vertical" as const,
  minHeight: "60px",
};

// ---------------------------------------------------------------------------
// Table styling (used by all CRUD pages)
// ---------------------------------------------------------------------------

export const crudTableSx = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.8125rem",
  "& thead th": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bg-surface)",
    borderBottom: "1px solid var(--border-subtle)",
    px: "12px",
    py: "10px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "var(--text-tertiary)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  "& tbody tr": {
    borderBottom: "1px solid var(--border-subtle)",
    cursor: "pointer",
    transition: "background-color 100ms ease",
    "&:hover": {
      backgroundColor: "var(--bg-hover)",
    },
  },
  "& td": {
    px: "12px",
    py: "10px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    color: "var(--text-secondary)",
  },
};

// ---------------------------------------------------------------------------
// Page layout styles
// ---------------------------------------------------------------------------

export const crudPageContainerSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  backgroundColor: "var(--bg-surface)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "8px",
  position: "relative",
};

export const crudToolbarSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: "12px",
  py: "8px",
  borderBottom: "1px solid var(--border-subtle)",
  backgroundColor: "var(--bg-surface)",
  flexShrink: 0,
};

export const crudScrollAreaSx = {
  flex: 1,
  overflowY: "auto",
  overflowX: "auto",
};

// ---------------------------------------------------------------------------
// Selected row styling helper
// ---------------------------------------------------------------------------

export function selectedRowSx(isSelected: boolean) {
  return {
    backgroundColor: isSelected
      ? "var(--accent-dim) !important"
      : undefined,
    outline: isSelected
      ? "1px solid var(--accent-glow)"
      : "none",
    outlineOffset: -1,
  };
}

// ---------------------------------------------------------------------------
// Radio cell styles
// ---------------------------------------------------------------------------

export const radioInputSx = {
  accentColor: "var(--accent)",
  width: 14,
  height: 14,
  cursor: "pointer",
};

// ---------------------------------------------------------------------------
// Primary cell (bold name column) styles
// ---------------------------------------------------------------------------

export const primaryCellSx = {
  fontWeight: 600,
  color: "var(--text-primary) !important",
};

// ---------------------------------------------------------------------------
// Footer button styles
// ---------------------------------------------------------------------------

export const cancelBtnSx = {
  px: "20px",
  py: "8px",
  borderRadius: "8px",
  border: "1px solid var(--border-mid)",
  backgroundColor: "transparent",
  color: "var(--text-secondary)",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  "&:hover": { backgroundColor: "var(--bg-hover)" },
};

export const saveBtnSx = {
  px: "20px",
  py: "8px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "var(--accent)",
  color: "#FFFFFF",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  "&:hover": { opacity: 0.9 },
  "&:disabled": { opacity: 0.4, cursor: "default" },
};

export const deleteBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  px: "12px",
  py: "6px",
  borderRadius: "6px",
  border: "1px solid rgba(251,113,133,0.4)",
  bgcolor: "rgba(251,113,133,0.1)",
  color: "var(--signal-rose)",
  fontSize: "0.8125rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  "&:hover": { bgcolor: "rgba(251,113,133,0.2)", color: "#FCA5A5" },
};

// Inline confirmation button styles (cancel / confirm-delete pairs)
export const confirmCancelBtnSx = {
  px: "16px",
  py: "6px",
  borderRadius: "8px",
  border: "1px solid var(--border-mid)",
  backgroundColor: "transparent",
  color: "var(--text-secondary)",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  "&:hover": { backgroundColor: "var(--bg-hover)" },
};

// ---------------------------------------------------------------------------
// Tab styling (used by BulkEditDrawer / EditInteractionDrawer)
// ---------------------------------------------------------------------------

export type TabId = "retention" | "tags" | "views";

export const tabSx = (active: boolean) => ({
  px: "16px",
  py: "8px",
  borderRadius: "6px 6px 0 0",
  border: "none",
  borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
  backgroundColor: "transparent",
  color: active ? "var(--text-primary)" : "var(--text-tertiary)",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  "&:hover": {
    color: "var(--text-primary)",
  },
});

// ---------------------------------------------------------------------------
// Close button styling (shared X-icon button in flyout drawers)
// ---------------------------------------------------------------------------

export const closeButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "6px",
  border: "none",
  backgroundColor: "transparent",
  color: "var(--text-tertiary)",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
  },
};

// ---------------------------------------------------------------------------
// Section badge styles (add/remove section headers)
// ---------------------------------------------------------------------------

export const sectionBadgeSx = (variant: "add" | "remove") => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  borderRadius: "50%",
  backgroundColor: variant === "add" ? "var(--accent-dim)" : "rgba(251,113,133,0.15)",
  color: variant === "add" ? "var(--accent)" : "var(--signal-rose)",
  fontSize: "0.75rem",
  fontWeight: 700,
  lineHeight: 1,
});

// ---------------------------------------------------------------------------
// Chip styles (tag/view chips with remove button)
// ---------------------------------------------------------------------------

export const chipSx = (variant: "add" | "remove") => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  px: "8px",
  py: "3px",
  borderRadius: "12px",
  backgroundColor: variant === "add" ? "var(--accent-dim)" : "rgba(251,113,133,0.1)",
  border: variant === "add" ? "1px solid var(--accent-glow)" : "1px solid rgba(251,113,133,0.25)",
  color: variant === "add" ? "var(--accent)" : "var(--signal-rose)",
  fontSize: "0.75rem",
  fontWeight: 500,
});

export const chipRemoveBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  border: "none",
  bgcolor: "transparent",
  color: "inherit",
  cursor: "pointer",
  p: 0,
  ml: "2px",
};

export const neutralChipRemoveBtnSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 14,
  height: 14,
  border: "none",
  backgroundColor: "transparent",
  color: "var(--text-tertiary)",
  cursor: "pointer",
  padding: 0,
  "&:hover": { color: "var(--text-primary)" },
};

// ---------------------------------------------------------------------------
// Inline drawer styles (slide-in panel, not MUI Drawer)
// ---------------------------------------------------------------------------

export const inlineDrawerSx = (animationName: string) => ({
  width: DRAWER_WIDTH,
  minWidth: DRAWER_WIDTH,
  flexShrink: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "var(--bg-surface)",
  borderLeft: "1px solid var(--border-mid)",
  color: "var(--text-primary)",
  overflow: "hidden",
  animation: `${animationName} 200ms ease`,
  [`@keyframes ${animationName}`]: {
    from: { transform: "translateX(100%)", opacity: 0 },
    to: { transform: "translateX(0)", opacity: 1 },
  },
});

export const drawerHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderBottom: "1px solid var(--border-subtle)",
  flexShrink: 0,
};

export const drawerTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "var(--text-primary)",
};

export const drawerTabBarSx = {
  display: "flex",
  gap: 0,
  borderBottom: "1px solid var(--border-subtle)",
  px: "20px",
  flexShrink: 0,
};

export const drawerScrollAreaSx = {
  flex: 1,
  overflowY: "auto",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

// ---------------------------------------------------------------------------
// Flyout title / save-label helpers (used by CRUD pages)
// ---------------------------------------------------------------------------

export function flyoutLabels(
  flyoutMode: "create" | "edit",
  isSaving: boolean,
  keys: { createTitle: string; editTitle: string; creating: string; saving: string; saveChanges: string },
) {
  const flyoutTitle = flyoutMode === "create" ? keys.createTitle : keys.editTitle;
  const saveLabel = isSaving
    ? (flyoutMode === "create" ? keys.creating : keys.saving)
    : (flyoutMode === "create" ? keys.createTitle : keys.saveChanges);
  return { flyoutTitle, saveLabel };
}

// ---------------------------------------------------------------------------
// Form error display style
// ---------------------------------------------------------------------------

export const formErrorSx = {
  color: "var(--signal-rose)",
  fontSize: "0.8125rem",
};

export const confirmDeleteBtnSx = {
  px: "16px",
  py: "6px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "var(--signal-rose)",
  color: "#FFFFFF",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  "&:hover": { opacity: 0.9 },
  "&:disabled": { opacity: 0.4, cursor: "default" },
};
